import { calculateIrrigationDetails } from "./irrigation";
import { FertilizerProduct } from "@/types/products";
import { IrrigationSystem } from "@/types/irrigationSystem";
import { InputRates } from "@/types/operations";
import { roundTo } from "./numbers";

export type InputRateKey = "rate" | "rate_1" | "rate_2";

export function hasInputRates(
    inputRates: InputRates | null | undefined,
): boolean {
    return !!inputRates?.rates?.length;
}

export function inputRatesToApplicationTableRows(inputRates: InputRates) {
    return inputRates.rates.map((entry) => ({
        target_rate_n: entry.rate ?? undefined,
        start: entry.start ?? undefined,
        stop: entry.stop ?? undefined,
        irrigated: entry.irrigated ?? undefined,
        treatment: entry.treatment ?? undefined,
        id: entry.zone ?? undefined,
        zone_number: entry.zone ?? undefined,
        row: entry.row ?? undefined,
    }));
}

export function inputRatesToPlantingTableRows(inputRates: InputRates) {
    return inputRates.rates.map((entry) => ({
        zone: null as number | null,
        zone_id: entry.zone ?? null,
        depth: entry.rate_2 ?? 0,
        seeding_rate: entry.rate_1 ?? entry.rate ?? 0,
    }));
}

export function inputRatesToHarvestTableRows(inputRates: InputRates) {
    return inputRates.rates.map((entry) => ({
        zone: null as number | null,
        zone_id: entry.zone ?? null,
        moisture: entry.rate_2 ?? 0,
        dry_yield: entry.rate_1 ?? entry.rate ?? 0,
    }));
}

export function formatInputRates(
    inputRates: InputRates | null | undefined,
    rateKey: InputRateKey,
    precision = 1,
): string | null {
    if (!inputRates?.rates?.length) return null;

    const values = inputRates.rates
        .map((entry) => entry[rateKey])
        .filter((value): value is number => value != null);

    if (values.length === 0) return null;

    const roundedValues = values.map((value) => roundTo(value, precision));
    const uniqueValues = [...new Set(roundedValues)];

    if (uniqueValues.length === 1) {
        return uniqueValues[0].toString();
    }

    return `${Math.min(...uniqueValues)} - ${Math.max(...uniqueValues)}`;
}

export function getInputRateNumericValue(
    inputRates: InputRates | null | undefined,
    rateKey: InputRateKey,
): number | null {
    if (!inputRates?.rates?.length) return null;

    const values = inputRates.rates
        .map((entry) => entry[rateKey])
        .filter((value): value is number => value != null);

    if (values.length === 0) return null;

    return values.reduce((sum, value) => sum + value, 0) / values.length;
}

export function hasFileSource(operation?: {
    source_id?: string | null;
    file_name?: string | null;
}) {
    return !!(operation?.source_id || operation?.file_name);
}

export function targetRateNToProdutcRate(
    target_rate_n?: number,
    selectedProduct?: FertilizerProduct,
) {
    if (!selectedProduct || target_rate_n == undefined || target_rate_n == null)
        return;

    let productRate;
    if (selectedProduct.fertilizer_type == 1) {
        if (selectedProduct.special_key == "NH3") {
            productRate = target_rate_n / 1;
        } else {
            productRate = target_rate_n / (selectedProduct.percent_N / 100);
        }
    } else if (selectedProduct.fertilizer_type == 2) {
        productRate =
            target_rate_n /
            parseFloat(selectedProduct.lbs_N_per_gallon?.toString());
    }
    return productRate;
}

export function productRateToTargetRateN(
    productRate?: number,
    selectedProduct?: FertilizerProduct,
) {
    if (!selectedProduct || productRate == undefined || productRate == null)
        return;
    let targetRate;
    if (selectedProduct.fertilizer_type === 1) {
        if (selectedProduct.special_key === "NH3") {
            targetRate = productRate * 1;
        } else {
            targetRate = productRate * (selectedProduct.percent_N / 100);
        }
    } else if (selectedProduct.fertilizer_type === 2) {
        targetRate =
            productRate *
            parseFloat(selectedProduct.lbs_N_per_gallon?.toString());
    }
    return targetRate;
}

export function speedToDepth(
    speed?: number,
    irrigationSystem?: IrrigationSystem,
): number | undefined {
    if (!irrigationSystem || speed == undefined || speed == null) return;

    const depth = calculateIrrigationDetails({
        pivotSpeedPercent: speed,
        pivotMinDepth: irrigationSystem.pivot_minimum_depth,
        irrigationDepth: undefined,
    }).irrigationDepth;
    return depth ? Number(depth) : undefined;
}

export function depthToSpeed(
    depth?: number,
    irrigationSystem?: IrrigationSystem,
): number | undefined {
    if (!irrigationSystem || depth == undefined || depth == null) return;
    let speed;
    if (depth > 0) {
        speed = (
            (parseFloat(irrigationSystem.pivot_minimum_depth?.toString()) /
                depth) *
            100
        ).toFixed(2);
    } else {
        speed = 0.0;
    }
    return speed ? Number(speed) : undefined;
}

export const calculateInjectionRate = ({
    nRateGalsPerAcre = 0,
    irrigatedArea = 0,
    pivotMinRevTime = 0,
    pivotSpeedAsPercent = 0,
    precision = 0,
}: {
    nRateGalsPerAcre?: number;
    irrigatedArea?: number;
    pivotMinRevTime?: number;
    pivotSpeedAsPercent?: number;
    precision?: number;
}): string => {
    const numsAfterDecimal: number = precision >= 0 ? precision : 2;

    const emptyNum = 0;
    let rateCalculation: string = emptyNum.toFixed(numsAfterDecimal);

    if (
        pivotMinRevTime > 0 &&
        pivotSpeedAsPercent > 0 &&
        nRateGalsPerAcre > 0 &&
        irrigatedArea > 0
    ) {
        rateCalculation = (
            nRateGalsPerAcre *
            (irrigatedArea / (pivotMinRevTime / (pivotSpeedAsPercent / 100)))
        ).toFixed(numsAfterDecimal);
    }

    return rateCalculation;
};
